/**
 * What the GPU can actually do to a texture, published by the renderer for the systems that build
 * textures.
 *
 * This exists because of an ordering problem with no tidy answer. Anisotropic filtering is capped by
 * the driver — `EXT_texture_filter_anisotropic` reports 2 on some integrated parts and 16 on most
 * discrete ones — so the only correct value is one that has to be read off a live GL context. But
 * `src/terrain/` builds its textures from plain data with no renderer in hand, and threading a
 * renderer down to a `DataArrayTexture` constructor to ask it one number would put a concrete
 * Three.js renderer into modules that are deliberately kept clear of one (see `Renderer`).
 *
 * So the renderer pushes the number here on construction and texture builders read it. The default
 * is the value that was hard-coded before this module existed, which means a headless test or a
 * texture built before any renderer exists still gets sensible filtering rather than none.
 */

/** What is used when no renderer has reported in. Every GL implementation supports at least this. */
const DEFAULT_MAX_ANISOTROPY = 8;

/**
 * Ceiling on what is requested even where the driver offers more.
 *
 * Past 16 the returns are not visible on ground textures at garden scale, and the cost is paid on
 * every one of the fragments that covers the largest surface in the scene.
 */
const ANISOTROPY_CEILING = 16;

let maxAnisotropy = DEFAULT_MAX_ANISOTROPY;

/** Called by the renderer once it has a GL context. Values below 1 are ignored as unreported. */
export function reportMaxAnisotropy(value: number | undefined): void {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 1) return;
  maxAnisotropy = Math.min(ANISOTROPY_CEILING, Math.max(1, Math.floor(value)));
}

/**
 * Anisotropy to request for a tiled ground texture — the worst case in the scene. A driveway seen
 * down its length is a minification of forty or more along one axis and one or two along the other,
 * which is precisely the case isotropic mip filtering blurs into mush.
 */
export function groundAnisotropy(): number {
  return maxAnisotropy;
}

/** Reset to the default. For tests, which must not leak one suite's fake GPU into the next. */
export function resetTextureCapabilities(): void {
  maxAnisotropy = DEFAULT_MAX_ANISOTROPY;
}
